import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  ListItemButton,
  Stack,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Person,
  Settings,
  ExitToApp,
  AccountBalance,
  EmojiEvents,
  People,
  Brightness4,
  Brightness7,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../util/auth';
import { useDarkMode } from '../util/theme';
import BonusWatcherLogo from './BonusWatcherLogo';

const pages = [
  { name: 'Bonus Watcher', path: '/bonus-watcher', icon: <AccountBalance /> },
  { name: 'Leaderboard', path: '/leaderboard', icon: <EmojiEvents /> },
  { name: 'Friends', path: '/friends', icon: <People /> },
];

const settings = [
  { name: 'Profile', path: '/settings/profile', icon: <Person /> },
  { name: 'Settings', path: '/settings/account', icon: <Settings /> },
];

const ResponsiveNavbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { value: isDark, toggle: toggleDarkMode } = useDarkMode();

  // Don't render anything for non-mobile users
  if (!isMobile) {
    return null;
  }

  const handleOpenUserMenu = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setUserMenuAnchor(null);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth/signin');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <AppBar 
      position="fixed" 
      elevation={0} 
      sx={{ 
        backdropFilter: 'blur(20px)',
        backgroundColor: theme.palette.mode === 'dark' 
          ? 'rgba(22, 28, 36, 0.8)'
          : 'rgba(255, 255, 255, 0.8)',
        borderBottom: `1px solid ${theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.05)'
        }`,
        height: '48px',
        display: 'flex',
        alignItems: 'center',
        padding: '0 4px'
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          gap: '2px'
        }}
      >
        <IconButton
          onClick={() => setMobileMenuOpen(true)}
          size="small"
          sx={{
            padding: 0,
            height: '38px',
            width: '38px',
            color: theme.palette.mode === 'dark' ? '#fff' : '#000',
          }}
        >
          <MenuIcon sx={{ fontSize: 38 }} />
        </IconButton>
        
        <BonusWatcherLogo 
          sx={{ 
            height: 38,
            flexGrow: 1
          }} 
        />
        
        <Stack direction="row" spacing={1.5} alignItems="center">
          <IconButton
            onClick={toggleDarkMode}
            size="small"
            sx={{
              padding: 0,
              height: '38px',
              width: '38px',
              color: theme.palette.mode === 'dark' ? '#fff' : '#000',
            }}
          >
            {isDark ? <Brightness7 sx={{ fontSize: 38 }} /> : <Brightness4 sx={{ fontSize: 38 }} />}
          </IconButton>
          
          <IconButton 
            onClick={handleOpenUserMenu}
            size="small"
            sx={{
              padding: 0,
              height: '38px',
              width: '38px'
            }}
          >
            <Avatar 
              alt={user?.email} 
              src={user?.photoURL}
              sx={{ 
                width: 38,
                height: 38,
                fontSize: '20px'
              }}
            >
              {user?.email?.[0]?.toUpperCase()}
            </Avatar>
          </IconButton>
        </Stack>
      </Box>

      <Menu
        id="menu-appbar"
        anchorEl={userMenuAnchor}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(userMenuAnchor)}
        onClose={handleCloseUserMenu}
      >
        {settings.map((setting) => (
          <MenuItem
            key={setting.name}
            onClick={() => {
              handleNavigation(setting.path);
              handleCloseUserMenu();
            }}
          >
            <Typography textAlign="center">{setting.name}</Typography>
          </MenuItem>
        ))}
        <MenuItem onClick={handleLogout}>
          <Typography textAlign="center">Logout</Typography>
        </MenuItem>
      </Menu>
      
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      >
        <Box sx={{ width: 250, p: 2 }}>
          <List>
            {pages.map((page) => (
              <ListItem key={page.name} disablePadding>
                <ListItemButton onClick={() => handleNavigation(page.path)}>
                  <ListItemIcon>{page.icon}</ListItemIcon>
                  <ListItemText primary={page.name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default ResponsiveNavbar; 